/**
 * web/src/components/shared/Badge.tsx
 * Badge matching `.badge` (design_files/styles.css lines 542-577). 11/500,
 * 2×7 padding, 4px radius, 1px border, optional 6px dot prefix. Status
 * variants reuse the running/waiting/completed/failed/cancelled palette.
 * The `role` variant is uppercase 10/600 neutral for role chips.
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * cva variant table for Badge. Status palette is the same status semantics
 * used across Dot / StatusBadge / Progress.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium leading-tight whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'bg-bg-muted border-border text-text-muted',
        running: 'bg-running-soft border-running-border text-running',
        waiting: 'bg-waiting-soft border-waiting-border text-waiting',
        awaiting: 'bg-awaiting-soft border-awaiting-border text-awaiting',
        completed: 'bg-completed-soft border-completed-border text-completed',
        failed: 'bg-failed-soft border-failed-border text-failed',
        cancelled: 'bg-cancelled-soft border-border text-cancelled',
        role: 'bg-bg-muted border-border text-text-muted text-[10px] font-semibold uppercase tracking-wider',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

/** Public props for Badge — span attributes plus `variant`. */
export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  children?: ReactNode;
}

/**
 * Hand-rolled Badge primitive.
 * Side effects: none — pure styled wrapper around a span. Children are
 * rendered via React's text-escaping (no dangerouslySetInnerHTML).
 */
export function Badge({ className, variant, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {children}
    </span>
  );
}
